export enum StatusEnum {
  PENDING = "PENDING",
  SAVED = "SAVED",
  DONE = "DONE",
}

export enum CampaignStatus {
  DRAFT = "DRAFT",
  ACTIVE = "ACTIVE",
  STOPPED = "STOPPED",
}

export enum FileTypeEnum {
  ID_PASSPORT = "id_passport",
  WEDDING_DIVORCE = "wedding_divorce",
  POLICE_RC = "police_rc",
  LSV = "lsv",
}

// Auth types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  message: string;
}

// Settings types
export interface Settings {
  id: number;
  default_reminder_interval_days: number;
  max_reminders_allowed: number;
  token_expiration_days: number;
  campaign_retention_days: number;
}

export interface SettingsUpdate {
  default_reminder_interval_days?: number;
  max_reminders_allowed?: number;
  token_expiration_days?: number;
  campaign_retention_days?: number;
}

// Campaign types
export interface ResidentInput {
  external_id: string;
  email?: string;
  name: string;
  object_name?: string;
}

export interface CampaignCreate {
  building_address: string;
  building_id: string;
  is_ppe?: boolean;
  residents: ResidentInput[];
}

export interface CampaignCreateResponse {
  campaign_id: number;
  created_count: number;
}

export interface ResidentLink {
  id: number;
  external_id: string;
  name: string;
  email?: string;
  token: string;
  link: string;
  status: StatusEnum;
}

// Structured Response Types
export interface IndividualTenant {
  status: "particulier";
  tenant_index?: number; // Optional for maintaining order when submitting
  civilite?: string;
  nom: string;
  prenom: string;
  date_naissance?: string;
  nationalite?: string;
  email?: string;
  telephone?: string;
  tel_professionnel?: string;
  tel_portable?: string;
  rue?: string;
  numero?: string;
  npa?: number;
  ville?: string;
  correspondance_email?: boolean;
  etat_civil?: string;
  is_guarantor?: boolean;
}

export interface EnterpriseTenant {
  status: "entreprise";
  tenant_index?: number; // Optional for maintaining order when submitting
  nom_societe: string;
  tel_professionnel?: string;
  tel_portable?: string;
  civilite?: string;
  nom_representant: string;
  prenom_representant: string;
  email_representant?: string;
  rue?: string;
  numero?: string;
  npa?: number;
  ville?: string;
  correspondance_email?: boolean;
  is_guarantor?: boolean;
}

export interface SharedFormFields {
  cave?: string;
  parking?: string;
  grenier?: string;
  nom_banque?: string;
  titulaire_compte?: string;
  iban?: string;
  nom_assurance_rc?: string;
  numero_police_rc?: string;
}

export interface FileUrlInfo {
  url: string;
  file_type?: string | null;
  original_filename?: string | null;
}

export interface StructuredResponseData {
  particulier_tenants: IndividualTenant[];
  enterprise_tenants: EnterpriseTenant[];
  // Shared fields (flattened)
  cave?: string;
  parking?: string;
  grenier?: string;
  nom_banque?: string;
  titulaire_compte?: string;
  iban?: string;
  nom_assurance_rc?: string;
  numero_police_rc?: string;
  comment?: string;
  // PPE-specific fields
  numero_appartement?: string;
  autre?: string;
  clearing?: string;
  locataire_nom?: string;
  locataire_prenom?: string;
  locataire_tel_prive?: string;
  locataire_tel_portable?: string;
  locataire_gerance?: string;
  moyen_paiement?: string;
  file_urls: FileUrlInfo[];
  updated_at: string;
}

export interface ResidentResponse {
  id: number;
  external_id: string;
  name: string;
  email?: string;
  status: StatusEnum;
  reminder_count: number;
  last_sent_at: string | null;
  response: StructuredResponseData | null;
}

export interface CampaignStats {
  total_campaigns: number;
  total_residents: number;
  pending_count: number;
  saved_count: number;
  done_count: number;
}

// Form types
export interface FormMeta {
  building_address: string;
  resident_name: string;
  object_name?: string;
  is_ppe?: boolean;
}

export interface FileMetadata {
  file_key: string;
  file_type: string;
  metadata?: Record<string, unknown>;
}

export interface FormState {
  individual_tenants: Array<IndividualTenant & { tenant_index: number }>;
  enterprise_tenants: Array<EnterpriseTenant & { tenant_index: number }>;
  cave?: string;
  parking?: string;
  grenier?: string;
  nom_banque?: string;
  titulaire_compte?: string;
  iban?: string;
  nom_assurance_rc?: string;
  numero_police_rc?: string;
  comment?: string;
  // PPE-specific fields
  numero_appartement?: string;
  autre?: string;
  clearing?: string;
  locataire_nom?: string;
  locataire_prenom?: string;
  locataire_tel_prive?: string;
  locataire_tel_portable?: string;
  locataire_gerance?: string;
  moyen_paiement?: string;
  file_keys: FileMetadata[] | null;
  status: StatusEnum;
}

export interface FormSubmitRequest {
  individual_tenants: IndividualTenant[];
  enterprise_tenants: EnterpriseTenant[];
  cave?: string;
  parking?: string;
  grenier?: string;
  nom_banque?: string;
  titulaire_compte?: string;
  iban?: string;
  nom_assurance_rc?: string;
  numero_police_rc?: string;
  comment?: string;
  // PPE-specific fields
  numero_appartement?: string;
  autre?: string;
  clearing?: string;
  locataire_nom?: string;
  locataire_prenom?: string;
  locataire_tel_prive?: string;
  locataire_tel_portable?: string;
  locataire_gerance?: string;
  moyen_paiement?: string;
  file_keys: FileMetadata[];
  is_final: boolean;
}

export interface FormSubmitResponse {
  status: StatusEnum;
  message: string;
}

export interface UploadUrlRequest {
  filename: string;
  file_type: FileTypeEnum;
}

export interface UploadUrlResponse {
  upload_url: string;
  object_key: string;
  file_type: string;
}

export interface ViewFileRequest {
  file_key: string;
}

export interface ViewFileResponse {
  view_url: string;
}

export interface Campaign {
  id: number;
  building_address: string;
  building_id: string;
  status: CampaignStatus;
  created_at: string;
}

export interface CampaignListItem {
  id: number;
  building_address: string;
  building_id: string;
  is_ppe: boolean;
  status: CampaignStatus;
  created_at: string;
  resident_count: number;
  pending_count: number;
  saved_count: number;
  done_count: number;
}

export interface CampaignUpdate {
  building_address?: string;
  building_id?: string;
  is_ppe?: boolean;
  residents?: ResidentInput[];
}

export interface CampaignStartResponse {
  campaign_id: number;
  status: CampaignStatus;
  emails_sent: number;
  message: string;
}

export interface CampaignStopResponse {
  campaign_id: number;
  status: CampaignStatus;
  message: string;
}

export interface FileUpload {
  file: File;
  type: FileTypeEnum;
  fileMetadata?: FileMetadata; // Stores file_key and file_type
  uploading?: boolean;
  error?: string;
}
